import React, { useState } from 'react';
import TryItPanel from './TryItPanel';
import RequestSnippets from './RequestSnippets';

function CopyBtn({ text, small }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    });
  };
  return (
    <button onClick={copy} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '3px 8px' : '4px 10px',
      fontSize: small ? 10 : 11, fontWeight: 500,
      fontFamily: 'Inter, sans-serif',
      border: '1px solid var(--border)',
      borderRadius: 6, cursor: 'pointer',
      background: done ? 'var(--success-bg)' : 'var(--bg-hover)',
      color: done ? 'var(--success)' : 'var(--text-muted)',
      transition: 'all 0.2s',
    }}>
      {done
        ? <><svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Copied!</>
        : <><svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" /><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" /></svg>Copy</>}
    </button>
  );
}

function JsonLine({ line }) {
  const m = line.match(/^(\s*)("[\w_-]+")\s*:\s*(.*)/);
  if (m) {
    const [, indent, key, rest] = m;
    const val = rest.replace(/,\s*$/, '');
    const comma = rest.trim().endsWith(',') ? ',' : '';
    let valEl;
    if (val === 'true' || val === 'false' || val === 'null') {
      valEl = <span style={{ color: 'var(--syn-bool)' }}>{val}</span>;
    } else if (!isNaN(val) && val !== '') {
      valEl = <span style={{ color: 'var(--syn-number)' }}>{val}</span>;
    } else if (val.startsWith('"')) {
      valEl = <span style={{ color: 'var(--syn-string)' }}>{val}</span>;
    } else {
      valEl = <span style={{ color: 'var(--text-secondary)' }}>{val}</span>;
    }
    return (
      <div>
        {indent}
        <span style={{ color: 'var(--syn-key)' }}>{key}</span>
        <span style={{ color: 'var(--text-muted)' }}>: </span>
        {valEl}
        <span style={{ color: 'var(--text-muted)' }}>{comma}</span>
      </div>
    );
  }
  return <div style={{ color: 'var(--text-muted)' }}>{line}</div>;
}

function CodeBlock({ code, label }) {
  return (
    <div style={{
      borderRadius: 12, overflow: 'hidden',
      border: '1px solid var(--border)',
      background: 'var(--code-bg)',
      minWidth: 0, width: '100%',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 14px',
        background: 'var(--code-header)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {['#ef4444','#f59e0b','#22c55e'].map((c, i) => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.7 }} />
          ))}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 8px' }}>{label || 'json'}</span>
        <CopyBtn text={code} small />
      </div>
      <pre style={{
        margin: 0, padding: '16px 18px',
        overflowX: 'auto', lineHeight: 1.75,
        fontSize: 12.5, fontFamily: 'JetBrains Mono, monospace',
        background: 'transparent',
        wordBreak: 'normal', whiteSpace: 'pre',
      }}>
        {code.split('\n').map((line, i) => <JsonLine key={i} line={line} />)}
      </pre>
    </div>
  );
}

function Tabs({ tabs, active, setActive, noMargin }) {
  return (
    <div style={{
      display: 'inline-flex', gap: 2, padding: 3,
      background: 'var(--bg-elevated)', borderRadius: 10,
      border: '1px solid var(--border)', marginBottom: noMargin ? 0 : 16,
    }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => setActive(t.id)}
          style={{
            padding: '5px 14px', borderRadius: 7, border: 'none',
            cursor: 'pointer', fontSize: 12.5, fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            background: active === t.id ? 'var(--bg-base)' : 'transparent',
            color: active === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: active === t.id ? 'var(--shadow-sm)' : 'none',
            transition: 'all 0.15s',
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

function RequiredBadge({ required }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      background: required ? 'var(--error-bg)' : 'var(--bg-elevated)',
      color: required ? 'var(--error)' : 'var(--text-muted)',
      border: `1px solid ${required ? 'var(--error-border)' : 'var(--border)'}`,
      fontFamily: 'Inter, sans-serif',
      flexShrink: 0, whiteSpace: 'nowrap', lineHeight: 1.6,
    }}>{required ? 'required' : 'optional'}</span>
  );
}

function TypeBadge({ type }) {
  const map = {
    string:  ['var(--type-string)', 'var(--type-string-bg)', 'var(--type-string-bd)'],
    object:  ['var(--type-object)', 'var(--type-object-bg)', 'var(--type-object-bd)'],
    array:   ['var(--type-array)', 'var(--type-array-bg)', 'var(--type-array-bd)'],
    number:  ['var(--type-number)', 'var(--type-number-bg)', 'var(--type-number-bd)'],
    integer: ['var(--type-number)', 'var(--type-number-bg)', 'var(--type-number-bd)'],
    boolean: ['var(--type-boolean)', 'var(--type-boolean-bg)', 'var(--type-boolean-bd)'],
  };
  const [col, bg, border] = map[type] || map.string;
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
      background: bg, color: col, border: `1px solid ${border}`,
      fontFamily: 'JetBrains Mono, monospace',
      flexShrink: 0, whiteSpace: 'nowrap', lineHeight: 1.6,
    }}>{type}</span>
  );
}

function ResponsePill({ code, type, active, onClick }) {
  const isSuccess = type === 'success';
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
        fontSize: 11.5, fontWeight: 700,
        fontFamily: 'JetBrains Mono, monospace',
        background: active
          ? (isSuccess ? 'var(--success)' : 'var(--error)')
          : 'var(--bg-elevated)',
        color: active ? '#fff' : (isSuccess ? 'var(--success)' : 'var(--error)'),
        boxShadow: active
          ? (isSuccess ? '0 2px 8px rgba(34,197,94,0.35)' : '0 2px 8px rgba(239,68,68,0.35)')
          : 'none',
        transition: 'all 0.15s',
      }}
    >{code}</button>
  );
}

function FieldRow({ field }) {
  return (
    <div style={{
      padding: '14px 16px',
      borderBottom: '1px solid var(--border-subtle)',
      background: 'transparent',
    }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div className="field-meta">
        <code style={{
          fontSize: 13, fontWeight: 700, color: 'var(--text-primary)',
          fontFamily: 'JetBrains Mono, monospace', flexShrink: 0,
          wordBreak: 'break-all',
        }}>{field.field || field.key}</code>
        <TypeBadge type={field.type || 'string'} />
        <RequiredBadge required={field.required} />
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: field.example ? 8 : 0, paddingLeft: 1 }}>
        {field.description}
      </div>
      {field.example && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, paddingLeft: 1, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, flexShrink: 0 }}>Example:</span>
          <code style={{
            fontSize: 11.5, color: 'var(--accent-light)',
            background: 'var(--accent-bg)', padding: '2px 9px',
            borderRadius: 5, fontFamily: 'JetBrains Mono, monospace',
            border: '1px solid var(--accent-glow)',
            wordBreak: 'break-all',
          }}>{field.example}</code>
        </div>
      )}
    </div>
  );
}

export default function ApiPage({ api }) {
  const [tab, setTab] = useState('body');
  const [respIdx, setRespIdx] = useState(0);

  const docTabs = [
    { id: 'body',    label: 'Body' },
    { id: 'headers', label: 'Headers' },
    { id: 'example', label: 'Example' },
  ];

  return (
    <div className="page-content page-content--api">

      <div style={{ marginBottom: 28 }}>
        <div className="endpoint-row">
          <span style={{
            fontSize: 11, fontWeight: 800, padding: '4px 12px', borderRadius: 8,
            background: 'var(--post-bg)', color: 'var(--post)',
            border: '1px solid var(--post-border)',
            fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.08em',
            flexShrink: 0,
          }}>POST</span>
          <code style={{
            fontSize: 13.5, color: 'var(--text-primary)',
            fontFamily: 'JetBrains Mono, monospace',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '5px 14px',
            maxWidth: '100%',
          }}>{api.endpoint}</code>
          <CopyBtn text={api.endpoint} small />
        </div>
        <h1 className="page-title page-title--api">
          {api.name}
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.75, maxWidth: 700 }}>
          {api.description}
        </p>
      </div>

      <div style={{
        border: '1px solid var(--border)',
        borderRadius: 14,
        background: 'var(--bg-surface)',
        overflow: 'visible',
        boxShadow: 'var(--shadow-md)',
      }}>
        <div className="api-split">

          <div className="api-split__docs">
            <div style={{ minHeight: 40, display: 'flex', alignItems: 'center', marginBottom: 16, overflowX: 'auto' }}>
              <Tabs tabs={docTabs} active={tab} setActive={setTab} noMargin />
            </div>

            {tab === 'headers' && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)' }}>
                {api.headers.map((h, i) => (
                  <div key={i} style={{
                    padding: '14px 16px',
                    borderBottom: i < api.headers.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <code style={{ fontSize: 13, color: 'var(--accent-light)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{h.key}</code>
                      <RequiredBadge required={h.required} />
                    </div>
                    {h.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 7 }}>{h.description}</div>}
                    <code style={{
                      fontSize: 11.5, color: 'var(--text-secondary)',
                      background: 'var(--bg-elevated)', padding: '2px 9px',
                      borderRadius: 5, fontFamily: 'JetBrains Mono, monospace',
                      border: '1px solid var(--border)',
                    }}>{h.value}</code>
                  </div>
                ))}
              </div>
            )}

            {tab === 'body' && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)' }}>
                {api.body.length === 0
                  ? <div style={{ padding: '20px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No request body required</div>
                  : api.body.map((f, i) => <FieldRow key={i} field={f} />)
                }
              </div>
            )}

            {tab === 'example' && (
              <>
                <RequestSnippets api={api} />
                <div style={{ marginTop: 20 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12,
                  }}>
                    Request Body
                  </div>
                  <CodeBlock code={api.requestExample} label="application/json" />
                </div>
              </>
            )}

            {api.notes?.length > 0 && (
              <div style={{
                marginTop: 20, padding: '14px 16px',
                background: 'var(--accent-bg)', border: '1px solid var(--accent-glow)',
                borderRadius: 12, borderLeft: '3px solid var(--accent)',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Notes</div>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {api.notes.map((n, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                      <span style={{ color: 'var(--accent)', flexShrink: 0 }}>↗</span>{n}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ marginTop: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>
                Response Examples
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
                {api.responses.map((r, i) => (
                  <ResponsePill key={i} code={r.code} type={r.type} active={respIdx === i} onClick={() => setRespIdx(i)} />
                ))}
              </div>
              {api.responses[respIdx] && (
                <CodeBlock code={api.responses[respIdx].example} label={`${api.responses[respIdx].code} · ${api.responses[respIdx].label}`} />
              )}
            </div>
          </div>

          <div className="api-split__try">
            <div className="try-sticky" style={{ position: 'sticky', top: 88 }}>
              <TryItPanel api={api} embedded />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
